import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics/track";
import type {
  MentorAvailabilityRule,
  MentorAvailabilityWindow,
  MentorBlackout,
  MentorBookingRequest,
} from "./types";

type BookingEmailEvent = "request" | "confirm" | "decline" | "cancel";

/**
 * Best-effort fan-out to the send-mentor-booking edge function. Never
 * throws — email delivery must not block the booking lifecycle UI.
 */
async function notifyBookingEmail(
  bookingId: string,
  event: BookingEmailEvent,
) {
  try {
    await supabase.functions.invoke("send-mentor-booking", {
      body: { booking_id: bookingId, event },
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[mentor-booking-email] invoke failed", event, err);
    }
  }
}

const k = {
  rules: (id: string) => ["mentor-rules", id],
  windows: (id: string) => ["mentor-windows", id],
  blackouts: (id: string) => ["mentor-blackouts", id],
  booked: (id: string) => ["mentor-booked-ranges", id],
};

export const useMentorAvailability = (mentorUserId: string | null) => {
  const enabled = !!mentorUserId;

  const rules = useQuery({
    queryKey: k.rules(mentorUserId ?? ""),
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_availability_rules")
        .select("*")
        .eq("mentor_user_id", mentorUserId!)
        .order("weekday");
      if (error) throw error;
      return (data ?? []) as MentorAvailabilityRule[];
    },
  });

  const windows = useQuery({
    queryKey: k.windows(mentorUserId ?? ""),
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_availability_windows")
        .select("*")
        .eq("mentor_user_id", mentorUserId!)
        .gte("ends_at", new Date().toISOString())
        .order("starts_at");
      if (error) throw error;
      return (data ?? []) as MentorAvailabilityWindow[];
    },
  });

  const blackouts = useQuery({
    queryKey: k.blackouts(mentorUserId ?? ""),
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_blackouts")
        .select("*")
        .eq("mentor_user_id", mentorUserId!)
        .gte("ends_at", new Date().toISOString())
        .order("starts_at");
      if (error) throw error;
      return (data ?? []) as MentorBlackout[];
    },
  });

  const bookedRanges = useQuery({
    queryKey: k.booked(mentorUserId ?? ""),
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_booking_requests")
        .select("proposed_start, proposed_end, status")
        .eq("mentor_user_id", mentorUserId!)
        .in("status", ["pending", "confirmed"])
        .gte("proposed_end", new Date().toISOString());
      if (error) throw error;
      return (data ?? []).map((r) => ({
        start: new Date(r.proposed_start),
        end: new Date(r.proposed_end),
      }));
    },
  });

  return { rules, windows, blackouts, bookedRanges };
};

export const useMentorAvailabilityMutations = (mentorUserId: string) => {
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: k.rules(mentorUserId) });
    qc.invalidateQueries({ queryKey: k.windows(mentorUserId) });
    qc.invalidateQueries({ queryKey: k.blackouts(mentorUserId) });
  };

  return {
    addRule: useMutation({
      mutationFn: async (
        row: Omit<MentorAvailabilityRule, "id" | "created_at" | "mentor_user_id">,
      ) => {
        const { data, error } = await supabase
          .from("mentor_availability_rules")
          .insert({ ...row, mentor_user_id: mentorUserId })
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      onSuccess: invalidate,
    }),
    deleteRule: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase
          .from("mentor_availability_rules")
          .delete()
          .eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    addWindow: useMutation({
      mutationFn: async (
        row: Omit<MentorAvailabilityWindow, "id" | "created_at" | "mentor_user_id">,
      ) => {
        const { data, error } = await supabase
          .from("mentor_availability_windows")
          .insert({ ...row, mentor_user_id: mentorUserId })
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      onSuccess: invalidate,
    }),
    deleteWindow: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase
          .from("mentor_availability_windows")
          .delete()
          .eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
    addBlackout: useMutation({
      mutationFn: async (
        row: Omit<MentorBlackout, "id" | "created_at" | "mentor_user_id">,
      ) => {
        const { data, error } = await supabase
          .from("mentor_blackouts")
          .insert({ ...row, mentor_user_id: mentorUserId })
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      onSuccess: invalidate,
    }),
    deleteBlackout: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase
          .from("mentor_blackouts")
          .delete()
          .eq("id", id);
        if (error) throw error;
      },
      onSuccess: invalidate,
    }),
  };
};

export const useBookingRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      mentorUserId: string;
      memberId: string;
      app: string;
      proposedStart: Date;
      proposedEnd: Date;
      message?: string;
    }) => {
      const { data, error } = await supabase
        .from("mentor_booking_requests")
        .insert({
          mentor_user_id: args.mentorUserId,
          member_id: args.memberId,
          app: args.app,
          proposed_start: args.proposedStart.toISOString(),
          proposed_end: args.proposedEnd.toISOString(),
          member_message: args.message ?? null,
        } as any)
        .select()
        .single();
      if (error) throw error;
      const row = data as MentorBookingRequest;
      void trackEvent("mentor_booking_request", {
        app: args.app as never,
        props: {
          booking_id: row.id,
          mentor_user_id: args.mentorUserId,
          duration_min: Math.round(
            (args.proposedEnd.getTime() - args.proposedStart.getTime()) / 60_000,
          ),
        },
      });
      void notifyBookingEmail(row.id, "request");
      return row;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mentor-booking-requests"] });
    },
  });
};

export const useMentorBookingRequests = (mentorUserId: string | null) =>
  useQuery({
    queryKey: ["mentor-booking-requests", mentorUserId],
    enabled: !!mentorUserId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_booking_requests")
        .select("*")
        .eq("mentor_user_id", mentorUserId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as MentorBookingRequest[];
    },
  });

export const useMyBookingRequests = (memberId: string | null) =>
  useQuery({
    queryKey: ["my-booking-requests", memberId],
    enabled: !!memberId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentor_booking_requests")
        .select("*")
        .eq("member_id", memberId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as MentorBookingRequest[];
    },
  });

export const useCancelBookingRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("mentor_booking_requests")
        .update({ status: "canceled", responded_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
      void trackEvent("mentor_booking_cancel", {
        props: { booking_id: id },
      });
      void notifyBookingEmail(id, "cancel");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-booking-requests"] });
      qc.invalidateQueries({ queryKey: ["mentor-booking-requests"] });
    },
  });
};

export const useRespondToBooking = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      action: "confirm" | "decline";
      mentorResponse?: string;
    }) => {
      const status = args.action === "confirm" ? "confirmed" : "declined";
      const { data, error } = await supabase
        .from("mentor_booking_requests")
        .update({
          status,
          mentor_response: args.mentorResponse ?? null,
          responded_at: new Date().toISOString(),
        })
        .eq("id", args.id)
        .select()
        .single();
      if (error) throw error;
      const row = data as MentorBookingRequest;
      const event = args.action === "confirm" ? "confirm" : "decline";
      void trackEvent(
        args.action === "confirm"
          ? "mentor_booking_confirm"
          : "mentor_booking_decline",
        {
          app: row.app as never,
          props: { booking_id: row.id },
        },
      );
      void notifyBookingEmail(row.id, event);
      return row;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mentor-booking-requests"] });
    },
  });
};
